import dynamic from 'next/dynamic';

import { useGalleryOnPage } from './GalleryOnPage.hook';
import type { GalleryOnPageProps } from './GalleryOnPage.typed';
import { GalleryView } from './components/GalleryView/GalleryView';
import { GalleryModal } from './components/GalleryModal/GalleryModal';
import { useGalleryView } from './components/GalleryView/GalleryView.hook';
import type { InlineGalleryViewProps } from './components/InlineGalleryView/InlineGalleryView.typed';

// Chunk-split the inline carousel (variants 1 and 2) so the control bundle
// stays untouched. SSR is on (next/dynamic default) so the hero <img fetchpriority="high">
// still ships in the initial HTML for the preload scanner.
const InlineGalleryView = dynamic<InlineGalleryViewProps>(() =>
  import('./components/InlineGalleryView/InlineGalleryView').then(
    (mod) => mod.InlineGalleryView,
  ),
);

const GALLERY_INTERACTIONS_VARIATION_1 = 'variation-1';
const GALLERY_INTERACTIONS_VARIATION_2 = 'variation-2';

function GalleryOnPage(props: GalleryOnPageProps) {
  const {
    className,
    photo,
    photos,
    videoConfig,
    link360,
    link360Interior,
    mediaCount,
    title,
    youTubeVideoId,
    youTubeReviewVideoId,
    isYoutubeReviewOpen,
    setIsYoutubeReviewOpen,
    isDealer,
    trackingId,
    reportOnGalleryUsage,
    cdnUrl,
    isPreview,
    refreshTopAndSideDFPUnit,
    galleryInteractionsVariant,
    ...rest
  } = props;

  const displayImage =
    videoConfig?.customProperties?.thumbnail_url_0 || (photo && photo.medium);

  const { openGallery, closeGallery, isGalleryOpen, initialIndexOverride } =
    useGalleryOnPage({
      reportOnGalleryUsage,
      displayImage,
    });

  // Todo move this into feature folder
  const {
    media,
    mediaPreview,
    photoCount,
    hasCarousel,
    mainGalleryMediaImage,
    mainGalleryMedia,
    galleryMedia,
    THUMBNAIL_360,
    getIndexGallery,
    handleOnMediaClick,
  } = useGalleryView({
    videoConfig,
    youTubeVideoId,
    youTubeReviewVideoId,
    link360,
    link360Interior,
    photos,
    isYoutubeReviewOpen,
  });

  const isCappedVariant =
    galleryInteractionsVariant === GALLERY_INTERACTIONS_VARIATION_1;
  const isInfiniteVariant =
    galleryInteractionsVariant === GALLERY_INTERACTIONS_VARIATION_2;

  const isInlineVariant = isCappedVariant || isInfiniteVariant;

  return (
    <>
      {isInlineVariant ? (
        <InlineGalleryView
          title={title}
          photos={photos}
          media={media}
          photoCount={photoCount}
          galleryMedia={galleryMedia}
          THUMBNAIL_360={THUMBNAIL_360}
          setIsYoutubeReviewOpen={setIsYoutubeReviewOpen}
          isDisabled={Boolean(isPreview)}
          loop={isInfiniteVariant}
          openGallery={openGallery}
          onMediaClick={handleOnMediaClick}
        />
      ) : (
        <GalleryView
          title={title}
          photoCount={photoCount}
          media={mediaPreview}
          hasCarousel={hasCarousel}
          mainGalleryMediaImage={mainGalleryMediaImage}
          mainGalleryMedia={mainGalleryMedia}
          galleryMedia={galleryMedia}
          THUMBNAIL_360={THUMBNAIL_360}
          setIsYoutubeReviewOpen={setIsYoutubeReviewOpen}
          isDisabled={Boolean(isPreview)}
          openGallery={openGallery}
          onMediaClick={handleOnMediaClick}
        />
      )}
      {isGalleryOpen && (
        <GalleryModal
          isOpen={isGalleryOpen}
          askToClose={closeGallery}
          link360={link360}
          link360Interior={link360Interior}
          videoConfig={videoConfig}
          initialMediaIndex={initialIndexOverride ?? getIndexGallery()}
          cdnUrl={cdnUrl}
          refreshTopAndSideDFPUnit={refreshTopAndSideDFPUnit}
          title={title}
          photos={photos}
          {...rest}
          media={media}
        />
      )}
    </>
  );
}

export { GalleryOnPage };
